import { ExtractedDocumentData, ValidatedData } from '../types';
import { useSecurityService, AuditEventType } from './securityService';
import { authFetch } from './authFetch';

// API endpoints configuration
const API_ENDPOINTS = {
  DOCUMENTS: '/api/documents',
  AUGMENTUM_SUBMIT: '/api/augmentum/submit',
} as const;

// API response types
export interface DocumentLoadResponse {
  success: boolean;
  data?: ExtractedDocumentData;
  error?: string;
  message?: string;
}

export interface ValidationSubmitResponse {
  success: boolean;
  submissionId?: string;
  error?: string;
  message?: string;
}

export interface AugmentumSubmitResponse {
  success: boolean;
  transactionId?: string;
  error?: string;
  message?: string;
}

// Error types for better error handling
export class DocumentServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'DocumentServiceError';
  }
}

class DocumentService {


  /**
   * Load extracted document data from backend
   * Requirements: 4.3 - Load document data for validation
   */
  async loadDocument(documentId: string): Promise<ExtractedDocumentData> {
    const { secureApiCall, logUserAction } = useSecurityService();
    
    try {
      logUserAction(AuditEventType.DOCUMENT_LOADED, { documentId });
      
      const url = `${import.meta.env.VITE_API_BASE_URL}${API_ENDPOINTS.DOCUMENTS}/${documentId}`;
      
      const response = await secureApiCall<DocumentLoadResponse>(url, {
        method: 'GET',
      });

      if (!response.success || !response.data) {
        throw new DocumentServiceError(
          response.error || 'Failed to load document',
          'DOCUMENT_LOAD_FAILED',
          undefined,
          response
        );
      }

      // Backend returns processed data with transformedData (Phase 1)
      // transformedData contains cached AI transformation results
      return response.data;
    } catch (error) {
      logUserAction(AuditEventType.ERROR_OCCURRED, {
        operation: 'loadDocument',
        documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      if (error instanceof DocumentServiceError) {
        throw error;
      }

      throw new DocumentServiceError(
        'Failed to load document from server',
        'NETWORK_ERROR',
        undefined,
        error
      );
    }
  }

  /**
   * Persist HITL field corrections (Sprint M2).
   * Saves the clerk's edited field values + audit into the document via the corrections
   * endpoint (PATCH /api/documents/:id/status). Status is NOT changed here — value
   * persistence only; HITL completion advances status separately.
   * Requirements: 5.4 - Store validated data for audit trail
   */
  async submitValidation(validatedData: ValidatedData): Promise<string> {
    const { logUserAction } = useSecurityService();

    // No edits → nothing to persist (avoids an empty corrections request).
    if (!validatedData.changesSummary || validatedData.changesSummary.length === 0) {
      return validatedData.documentId;
    }

    try {
      logUserAction(AuditEventType.VALIDATION_SUBMITTED, {
        documentId: validatedData.documentId,
        fieldsChanged: validatedData.changesSummary.length,
      });

      // `fieldName` is carried alongside `fieldId` (Lens S5). `fieldId` is a POSITIONAL synthetic
      // (`field-${index}`, minted per load in useWorkspaceDocument), so on its own it identifies a
      // slot in an array, not a field: any reprocess or analyzer-schema change that reorders
      // `extractedFields` silently re-points every stored correction at a different field. The
      // correction history in the Lens document view is an audit surface, so it has to name the
      // field it actually belongs to. `changesSummary` already carries the name — it was simply
      // being dropped here.
      const corrections = validatedData.changesSummary.map((change) => ({
        fieldId: change.fieldId,
        fieldName: change.fieldName,
        originalValue: change.originalValue,
        correctedValue: change.newValue,
      }));

      const response = await authFetch(
        `/documents/${encodeURIComponent(validatedData.documentId)}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            corrections,
            validatedBy: validatedData.validatedBy,
          }),
        }
      );

      if (!response.ok) {
        throw new DocumentServiceError(
          `Failed to persist corrections: ${response.status} ${response.statusText}`,
          'VALIDATION_SUBMIT_FAILED',
          response.status
        );
      }

      const data = (await response.json()) as ValidationSubmitResponse;
      if (data.success === false) {
        throw new DocumentServiceError(
          data.error || 'Failed to persist corrections',
          'VALIDATION_SUBMIT_FAILED',
          undefined,
          data
        );
      }

      return validatedData.documentId;
    } catch (error) {
      logUserAction(AuditEventType.ERROR_OCCURRED, {
        operation: 'submitValidation',
        documentId: validatedData.documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      if (error instanceof DocumentServiceError) {
        throw error;
      }

      throw new DocumentServiceError(
        'Failed to submit validation to server',
        'NETWORK_ERROR',
        undefined,
        error
      );
    }
  }

  /**
   * Submit validated data to Augmentum/Momentum system
   * Requirements: 5.5 - Transmit ValidatedData to Augmentum/Momentum system
   */
  async submitToAugmentum(validatedData: ValidatedData): Promise<string> {
    const { secureApiCall, logUserAction } = useSecurityService();
    
    try {
      logUserAction(AuditEventType.VALIDATION_SUBMITTED, {
        documentId: validatedData.documentId,
        operation: 'augmentum_submit',
        fieldsChanged: validatedData.changesSummary.length,
      });

      const url = `${import.meta.env.VITE_API_BASE_URL}${API_ENDPOINTS.AUGMENTUM_SUBMIT}`;
      
      const response = await secureApiCall<AugmentumSubmitResponse>(url, {
        method: 'POST',
        body: JSON.stringify(validatedData),
      });

      if (!response.success || !response.transactionId) {
        throw new DocumentServiceError(
          response.error || 'Failed to submit to Augmentum system',
          'AUGMENTUM_SUBMIT_FAILED',
          undefined,
          response
        );
      }

      return response.transactionId;
    } catch (error) {
      logUserAction(AuditEventType.ERROR_OCCURRED, {
        operation: 'submitToAugmentum',
        documentId: validatedData.documentId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      if (error instanceof DocumentServiceError) {
        throw error;
      }

      throw new DocumentServiceError(
        'Failed to submit to Augmentum system',
        'NETWORK_ERROR',
        undefined,
        error
      );
    }
  }

  /**
   * Get document list for queue management
   * Additional utility for supervisors to manage document queue
   */
  async getDocumentQueue(status?: 'pending' | 'in_progress' | 'completed'): Promise<ExtractedDocumentData[]> {
    const { secureApiCall, logUserAction } = useSecurityService();
    
    try {
      const queryParams = status ? `?status=${status}` : '';
      const url = `${import.meta.env.VITE_API_BASE_URL}${API_ENDPOINTS.DOCUMENTS}${queryParams}`;
      
      const response = await secureApiCall<{ success: boolean; data: ExtractedDocumentData[]; error?: string }>(url, {
        method: 'GET',
      });

      if (!response.success || !response.data) {
        throw new DocumentServiceError(
          response.error || 'Failed to load document queue',
          'QUEUE_LOAD_FAILED',
          undefined,
          response
        );
      }

      logUserAction(AuditEventType.DOCUMENT_LOADED, {
        operation: 'getDocumentQueue',
        status,
        count: response.data.length,
      });

      return response.data;
    } catch (error) {
      logUserAction(AuditEventType.ERROR_OCCURRED, {
        operation: 'getDocumentQueue',
        status,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      if (error instanceof DocumentServiceError) {
        throw error;
      }

      throw new DocumentServiceError(
        'Failed to load document queue',
        'NETWORK_ERROR',
        undefined,
        error
      );
    }
  }

}

// Export singleton instance
export const documentService = new DocumentService();

// Hook for using document service with proper error handling
export const useDocumentService = () => {
  const handleError = (error: unknown): DocumentServiceError => {
    if (error instanceof DocumentServiceError) {
      return error;
    }

    // Surface the underlying error message so callers and the console show what actually failed,
    // rather than the opaque "An unexpected error occurred" that hides root cause.
    const message = error instanceof Error ? error.message : 'An unexpected error occurred';
    
    return new DocumentServiceError(
      message,
      'UNKNOWN_ERROR',
      undefined,
      error
    );
  };
  
  // Simple logging helper (doesn't require auth context)
  const logAction = (operation: string, details: any) => {
    console.log(`[DocumentService] ${operation}:`, details);
  };

  const loadDocument = async (documentId: string): Promise<ExtractedDocumentData> => {
    try {
      return await documentService.loadDocument(documentId);
    } catch (error) {
      throw handleError(error);
    }
  };

  const submitValidation = async (validatedData: ValidatedData): Promise<string> => {
    try {
      return await documentService.submitValidation(validatedData);
    } catch (error) {
      throw handleError(error);
    }
  };

  const submitToAugmentum = async (validatedData: ValidatedData): Promise<string> => {
    try {
      return await documentService.submitToAugmentum(validatedData);
    } catch (error) {
      throw handleError(error);
    }
  };

  const getDocumentQueue = async (status?: 'pending' | 'in_progress' | 'completed'): Promise<ExtractedDocumentData[]> => {
    try {
      return await documentService.getDocumentQueue(status);
    } catch (error) {
      throw handleError(error);
    }
  };

  const updateDocumentStatus = async (documentId: string, status: string): Promise<void> => {
    try {
      console.log('[DocumentService Hook] Updating document status:', { documentId, status });
      
      // Use apiService for direct API calls
      const { apiService } = await import('./apiService');
      
      const response = await apiService.updateDocumentStatus(documentId, status);
      console.log('[DocumentService Hook] Response from API:', response);

      if (!response.success) {
        console.error('[DocumentService Hook] API returned failure:', response);
        throw new DocumentServiceError(
          response.message || 'Failed to update document status',
          'STATUS_UPDATE_FAILED',
          undefined,
          response
        );
      }

      console.log('[DocumentService Hook] Status update successful');
      logAction('updateDocumentStatus', { documentId, status });
    } catch (error) {
      console.error('[DocumentService Hook] Error in updateDocumentStatus:', error);
      logAction('updateDocumentStatus_ERROR', {
        documentId,
        status,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      throw handleError(error);
    }
  };

  const getNextProcessedDocument = async (documentType?: string): Promise<ExtractedDocumentData | null> => {
    try {
      console.log('[DocumentService Hook] Getting next processed document', { documentType });
      
      // Use apiService for direct API calls
      const { apiService } = await import('./apiService');
      const response = await apiService.getNextProcessedDocument(documentType);
      console.log('[DocumentService Hook] Response from API:', response);

      // Handle response format
      if (!response.success) {
        throw new DocumentServiceError(
          response.message || 'Failed to get next document',
          'NEXT_DOCUMENT_FAILED',
          undefined,
          response
        );
      }

      const documentData = response.data || null;

      if (documentData) {
        logAction('getNextProcessedDocument', { documentId: documentData.documentId });
      } else {
        console.log('[DocumentService Hook] No next document available');
      }

      return documentData;
    } catch (error) {
      console.error('[DocumentService Hook] Error in getNextProcessedDocument:', error);
      logAction('getNextProcessedDocument_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      throw handleError(error);
    }
  };

  return {
    loadDocument,
    submitValidation,
    submitToAugmentum,
    getDocumentQueue,
    updateDocumentStatus,
    getNextProcessedDocument,
    DocumentServiceError,
  };
};